// Header.tsx
import { format } from 'date-fns'
import {
  Box,
  Flex,
  HStack,
  Button,
  Image,
  Text,
  useDisclosure,
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  DrawerHeader,
  DrawerBody,
  IconButton,
  VStack,
  useToast,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Avatar,
  Divider
} from '@chakra-ui/react'
import BigNumber from 'bignumber.js'
import { HamburgerIcon } from '@chakra-ui/icons'
import { useNavigate } from 'react-router-dom'
import { useRef, useMemo } from 'react'
import { authState } from '../../atoms/auth'
import { useRecoilState } from 'recoil'
import { useAuthState } from '../../utils/authUtils'
import { signOut } from '../../services/apiService'
import {
  FiLogOut,
  FiGlobe,
  FiSettings,
  FiUser
} from 'react-icons/fi'
import { useTranslation } from 'react-i18next'

const Header = () => {
  const navigate = useNavigate()
  const { isOpen, onOpen, onClose } = useDisclosure()
  const btnRef = useRef<HTMLButtonElement | null>(null)
  const [auth] = useRecoilState(authState)
  const toast = useToast()
  const { initUser } = useAuthState()
  const { t, i18n } = useTranslation()

  // 언어 목록
  const languages = useMemo(
    () => [
      { code: 'ko', label: t('한국어'), flag: '/images/flags/ko.png' },
      { code: 'en', label: t('영어'), flag: '/images/flags/en.png' },
      { code: 'ja', label: t('일본어'), flag: '/images/flags/ja.png' },
      { code: 'th', label: t('태국어'), flag: '/images/flags/th.png' },
      { code: 'vi', label: t('베트남어'), flag: '/images/flags/vi.png' },
      { code: 'zh', label: t('중국어'), flag: '/images/flags/zh.png' }
    ],
    [t]
  )

  const currentLang = i18n.language?.split('-')[0] || 'ko'
  const currentFlag =
    languages.find((l) => l.code === currentLang)?.flag ??
    '/images/flags/ko.png'

  const changeLanguage = async (lng: string) => {
    await i18n.changeLanguage(lng)
    try {
      localStorage.setItem('i18nextLng', lng)
    } catch {}
    toast({
      title: t('언어가 변경되었습니다.'),
      description: t('현재 언어') + `: ${lng.toUpperCase()}`,
      status: 'success',
      duration: 1500,
      isClosable: true
    })
  }

  const isValidFuture = (iso?: string | null) => {
    if (!iso) return false
    const t = Date.parse(iso)
    if (Number.isNaN(t)) return false
    return t > Date.now()
  }


  const handleClickLogout = async () => {
    try {
      const { data: res } = await signOut()
      if (res.statusCode === 200) {
        initUser()
        toast({
          title: t('로그아웃'),
          description: res.message,
          status: 'success',
          duration: 3000,
          isClosable: true
        })
        navigate('/')
      }
    } catch (error: any) {
      toast({
        title: t('오류'),
        description: error.response?.data?.message || '로그아웃에 실패했습니다.',
        status: 'error',
        duration: 3000,
        isClosable: true
      })
    }
  }

  return (
    <Box
      bg="white"
      w="100%"
      borderBottom="1px"
      borderColor="gray.200"
      px={4}
      py={2}
    >
      <Flex
        align="center"
        maxW="7xl"
        mx="auto"
        justify="space-between"
        height={'57px'}
      >
        {/* 왼쪽: 햄버거 + 로고 */}
        <Flex align="center">
          <IconButton
            ref={btnRef}
            icon={<HamburgerIcon />}
            aria-label={t('메뉴 열기')}
            variant="ghost"
            display={{ base: 'inline-flex', md: 'none' }}
            onClick={onOpen}
            mr={2}
          />
          <Flex align="center" cursor="pointer" onClick={() => navigate('/')}>
            <Image
              src="/images/logo.png"
              mr={2}
              height={'40px'}
              width={'auto'}
              alt="logo"
            />
          </Flex>
        </Flex>

        {/* 오른쪽: 데스크탑 메뉴 + 로그인 */}
        <Flex align="center" gap={4}>
          <HStack
            spacing={4}
            align="center"
            display={{ base: 'none', md: 'flex' }}
          >
            <Text
              _hover={{ color: 'brand.600', cursor: 'pointer' }}
              onClick={() => navigate('/introduction')}
            >
              {t('회사소개')}
            </Text>
            {auth.id && (
              <>
                <Text
                  _hover={{ color: 'brand.600', cursor: 'pointer' }}
                  onClick={() => navigate('/business-introduction')}
                >
                  {t('사업소개')}
                </Text>
                <Text
                  _hover={{ color: 'brand.600', cursor: 'pointer' }}
                  onClick={() => navigate('/auction-cases')}
                >
                  {t('경매 사례')}
                </Text>
                <Text
                  _hover={{ color: 'brand.600', cursor: 'pointer' }}
                  onClick={() => navigate('/assets')}
                >
                  {t('자산')}
                </Text>
                <Text
                  _hover={{ color: 'brand.600', cursor: 'pointer' }}
                  onClick={() => navigate('/community')}
                >
                  {t('커뮤니티')}
                </Text>
                <Text
                  _hover={{ color: 'brand.600', cursor: 'pointer' }}
                  onClick={() => navigate('/qna')}
                >
                  {t('Q&A')}
                </Text>
                <Text
                  _hover={{ color: 'brand.600', cursor: 'pointer' }}
                  onClick={() => navigate('/notice')}
                >
                  {t('공지사항')}
                </Text>
                <Text
                  _hover={{ color: 'brand.600', cursor: 'pointer' }}
                  onClick={() => navigate('/faq')}
                >
                  {t('FAQ')}
                </Text>
              </>
            )}
            {/* 관리자 메뉴 - 비활성화 */}
            {/* {auth.isAdmin === 1 && (
              <Text
                _hover={{ color: 'brand.600', cursor: 'pointer' }}
                onClick={() => navigate('/admin/user')}
                fontWeight="semibold"
                color="brand.600"
              >
                {t('관리자')}
              </Text>
            )} */}
          </HStack>

          {/* 언어 선택 (데스크톱) - 비활성화 */}
          {/* <Menu placement="bottom-end">
            <MenuButton
              as={Button}
              size="sm"
              variant="ghost"
              leftIcon={<FiGlobe />}
              rightIcon={
                <Image
                  src={currentFlag}
                  alt={currentLang}
                  boxSize="18px"
                  borderRadius="full"
                />
              }
            >
              {t('언어')}
            </MenuButton>
            <MenuList zIndex={1400}>
              {languages.map((lang) => (
                <MenuItem
                  key={lang.code}
                  onClick={() => changeLanguage(lang.code)}
                >
                  <HStack spacing={3}>
                    <Image
                      src={lang.flag}
                      alt={lang.label}
                      boxSize="18px"
                      borderRadius="full"
                    />
                    <Text>{lang.label}</Text>
                  </HStack>
                </MenuItem>
              ))}
            </MenuList>
          </Menu> */}

          {auth.id ? (
            <>
              <Menu>
                <MenuButton>
                  <Avatar
                    size="sm"
                    name={auth.username || undefined}
                    cursor="pointer"
                  />
                </MenuButton>
                <MenuList zIndex={1400}>
                  <Box px={4} py={3}>
                    <VStack spacing={3} align="flex-start">
                      <VStack spacing={1} align="flex-start" w="full">
                        <HStack>
                          <Text fontSize="sm" color="gray.500">
                            {t('아이디')}
                          </Text>
                          <Text
                            fontSize="sm"
                            fontWeight="medium"
                            color="gray.800"
                            isTruncated
                            maxW="140px"
                          >
                            {auth.account || '-'}
                          </Text>
                        </HStack>
                        <HStack>
                          <Text fontSize="sm" color="gray.500">
                            {t('사용자명')}
                          </Text>
                          <Text
                            fontSize="sm"
                            fontWeight="medium"
                            color="gray.800"
                            isTruncated
                            maxW="140px"
                          >
                            {auth.username || '-'}
                          </Text>
                        </HStack>
                      </VStack>
                    </VStack>
                  </Box>

                  <Divider my={3} />

                  {auth.isAdmin === 1 && (
                    <MenuItem onClick={() => navigate('/admin/user')}>
                      <HStack spacing={3} w="full">
                        <FiSettings pointerEvents="none" />
                        <Text>{t('관리자 메뉴')}</Text>
                      </HStack>
                    </MenuItem>
                  )}

                  <MenuItem onClick={() => navigate('/mypage')}>
                    <HStack spacing={3} w="full">
                      <FiUser pointerEvents="none" />
                      <Text>{t('마이페이지')}</Text>
                    </HStack>
                  </MenuItem>

                  <MenuItem onClick={handleClickLogout}>
                    <HStack spacing={3} w="full">
                      <FiLogOut pointerEvents="none" />
                      <Text>{t('로그아웃')}</Text>
                    </HStack>
                  </MenuItem>
                </MenuList>
              </Menu>
            </>
          ) : (
            <>
              <Button
                size="sm"
                colorScheme="brand"
                onClick={() => navigate('/signIn')}
              >
                {t('로그인')}
              </Button>
            </>
          )}
        </Flex>
      </Flex>

      {/* 모바일 드로어 메뉴 */}
      <Drawer
        isOpen={isOpen}
        placement="left"
        onClose={onClose}
        finalFocusRef={btnRef}
      >
        <DrawerOverlay />
        <DrawerContent>
          <DrawerCloseButton />
          <DrawerHeader>
            <Flex
              align="center"
              cursor="pointer"
              onClick={() => {
                navigate('/')
                onClose()
              }}
            >
              <Image
                src="/images/logo.png"
                mr={2}
                width={'auto'}
                height={'35px'}
                alt="logo"
              />
            </Flex>
          </DrawerHeader>

          <DrawerBody>
            {/* 언어 선택 (모바일 드로어 상단) - 비활성화 */}
            {/* <VStack align="flex-start" spacing={3} mb={4}>
              <Text fontWeight="bold">{t('언어')}</Text>
              <HStack wrap="wrap" spacing={3}>
                {languages.map((lang) => (
                  <Button
                    key={lang.code}
                    size="sm"
                    variant={currentLang === lang.code ? 'solid' : 'outline'}
                    leftIcon={
                      <Image
                        src={lang.flag}
                        alt={lang.label}
                        boxSize="16px"
                        borderRadius="full"
                      />
                    }
                    onClick={() => changeLanguage(lang.code)}
                  >
                    {lang.label}
                  </Button>
                ))}
              </HStack>
            </VStack> */}

            <VStack
              align="flex-start"
              spacing={4}
              fontSize="md"
              color="gray.700"
            >
              {auth.id && (
                <Text
                  onClick={() => {
                    navigate('/mypage')
                    onClose()
                  }}
                  cursor="pointer"
                  _hover={{ color: 'brand.600' }}
                >
                  {t('마이페이지')}
                </Text>
              )}
              <Text
                onClick={() => {
                  navigate('/introduction')
                  onClose()
                }}
                cursor="pointer"
                _hover={{ color: 'brand.600' }}
              >
                {t('회사소개')}
              </Text>
              {auth.id && (
                <>
                  <Text
                    onClick={() => {
                      navigate('/business-introduction')
                      onClose()
                    }}
                    cursor="pointer"
                    _hover={{ color: 'brand.600' }}
                  >
                    {t('사업소개')}
                  </Text>
                  <Text
                    onClick={() => {
                      navigate('/auction-cases')
                      onClose()
                    }}
                    cursor="pointer"
                    _hover={{ color: 'brand.600' }}
                  >
                    {t('경매 사례')}
                  </Text>
                  <Text
                    onClick={() => {
                      navigate('/assets')
                      onClose()
                    }}
                    cursor="pointer"
                    _hover={{ color: 'brand.600' }}
                  >
                    {t('자산')}
                  </Text>
                  <Text
                    onClick={() => {
                      navigate('/community')
                      onClose()
                    }}
                    cursor="pointer"
                    _hover={{ color: 'brand.600' }}
                  >
                    {t('커뮤니티')}
                  </Text>
                  <Text
                    onClick={() => {
                      navigate('/qna')
                      onClose()
                    }}
                    cursor="pointer"
                    _hover={{ color: 'brand.600' }}
                  >
                    {t('Q&A')}
                  </Text>
                  <Text
                    onClick={() => {
                      navigate('/notice')
                      onClose()
                    }}
                    cursor="pointer"
                    _hover={{ color: 'brand.600' }}
                  >
                    {t('공지사항')}
                  </Text>
                  <Text
                    onClick={() => {
                      navigate('/faq')
                      onClose()
                    }}
                    cursor="pointer"
                    _hover={{ color: 'brand.600' }}
                  >
                    {t('FAQ')}
                  </Text>
                </>
              )}
              {/* 관리자 메뉴 - 비활성화 */}
              {/* {auth.isAdmin === 1 && (
                <Text
                  onClick={() => {
                    navigate('/admin/user')
                    onClose()
                  }}
                  cursor="pointer"
                  _hover={{ color: 'brand.600' }}
                  fontWeight="semibold"
                  color="brand.600"
                >
                  {t('관리자')}
                </Text>
              )} */}
            </VStack>
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </Box>
  )
}

export default Header
